import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useLocalStorage } from 'react-use'

import { useUserOrganization } from '@modules-deprecated/app/organization/userOrganizationContext'
import { useUserOrganizationSettings } from '@modules-deprecated/app/organization/userOrganizationSettingsContext'
import { useUmbrellaStatus } from '@modules-deprecated/app/umbrellas/hooks/useUmbrellaStatus'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { useOrganizationSource } from '../../../hooks/useOrganizationSource'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { OnboardingPlatform } from '../../onboarding/enums/onboardingPlatform'
import { isEligibleForOnboarding } from '../../onboarding/utils/isEligibleForOnboarding'
import { isOnboardingFinalized } from '../../onboarding/utils/isOnboardingFinalized'

// All users have undefined onboardingCurrentStep before starting onboarding, so we can't distinguish
// new users from old ones by step alone. The 24h window targets only fresh registrations without
// risking redirecting existing users.
const NEW_USER_ONBOARDING_WINDOW = 24 * 60 * 60 * 1000

export const useOnboardingLoginRedirect = () => {
  const hasRedirectedRef = useRef(false)
  const pathnameRef = useRef<string>()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { organization } = useUserOrganization()
  const { isSourceBilly } = useOrganizationSource()
  const { hasUmbrellas: isUmbrellaMember } = useUmbrellaStatus()
  const { isFetched, organizationSettings } = useUserOrganizationSettings()
  const { url: onboardingUrl } = useOrganizationViewUrl(reactRoute.organization.onboarding.getMainRoute())
  const [onboardingFinalizedLocalStorage] = useLocalStorage(
    `${LocalStorageKeys.OnboardingFinalized}-${organization?.id ?? ''}`,
  )

  pathnameRef.current = pathname

  const orgCreatedTime = organization?.createdTime ? new Date(organization.createdTime).getTime() : null
  const isRecentRegistration = !!orgCreatedTime && Date.now() - orgCreatedTime < NEW_USER_ONBOARDING_WINDOW
  // Block rendering until org settings resolve, only for fresh registrations where a redirect may follow
  const isOnboardingRedirectPending = isRecentRegistration && !isFetched

  useEffect(() => {
    if (!isFetched || !onboardingUrl || hasRedirectedRef.current) {
      return
    }

    if (pathnameRef.current && pathnameRef.current.startsWith(onboardingUrl)) {
      return
    }

    const isFinalizedFromLocalStorage = !!organization?.id && onboardingFinalizedLocalStorage === true
    const isFinalizedFromOrganizationSettings =
      !!organizationSettings?.onboardingCurrentStep && isOnboardingFinalized(organizationSettings.onboardingCurrentStep)

    if (isFinalizedFromLocalStorage || isFinalizedFromOrganizationSettings) {
      return
    }

    const onboardingPlatform = organizationSettings?.onboardingPlatform
    const isWebPlatform = !onboardingPlatform || onboardingPlatform === OnboardingPlatform.Web

    if (!isWebPlatform) {
      return
    }

    const onboardingCurrentStep = organizationSettings?.onboardingCurrentStep
    const isOnboardingInProgress = !!onboardingCurrentStep && !isOnboardingFinalized(onboardingCurrentStep)
    const hasNotStartedOnboarding = !onboardingCurrentStep && isRecentRegistration

    if (!isOnboardingInProgress && !hasNotStartedOnboarding) {
      return
    }

    const shouldGoToOnboarding = isEligibleForOnboarding({ isSourceBilly, isUmbrellaMember, organization })

    if (shouldGoToOnboarding) {
      hasRedirectedRef.current = true
      navigate(onboardingUrl)
    }
  }, [
    navigate,
    isFetched,
    isRecentRegistration,
    isSourceBilly,
    isUmbrellaMember,
    onboardingFinalizedLocalStorage,
    onboardingUrl,
    organization,
    organizationSettings,
  ])

  return { isOnboardingRedirectPending }
}
